"use client";

import { type DragEvent, useState } from "react";
import { flushSync } from "react-dom";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
} from "@/components/tool-kit/styles";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseLines } from "@/lib/tool-kit/classroom/entries";
import { secureRandom } from "@/lib/tool-kit/classroom/random";
import {
  type Assignment,
  arrangeStudents,
  buildSeats,
  duplicateNames,
  findViolations,
  resolveConstraints,
  type Seat,
  type SeatingLayout,
  type SeatingOrder,
  seatRows,
  swapSeats,
} from "@/lib/tool-kit/classroom/seating";

type View = "teacher" | "student";
type LayoutKind = SeatingLayout["kind"];

const range = (from: number, to: number) =>
  Array.from({ length: to - from + 1 }, (_, index) => String(from + index));

const CHOICES = {
  rows: range(1, 12),
  columns: range(1, 12),
  tables: range(1, 16),
  seatsPerTable: range(2, 8),
  tablesPerRow: range(1, 6),
};

/** English strings. A translated content file overrides these through `ui`. */
export const SEATING_CHART_GENERATOR_STRINGS = {
  namesLabel: "Students — one name per line",
  namesPlaceholder: "Ava Brown\nBen Carter\nChloe Diaz",
  example:
    "Ava Brown\nBen Carter\nChloe Diaz\nDev Evans\nElla Fischer\nFinn Garcia\nGrace Huang\nHugo Ito\nIsla Jensen\nJamal King\nKira Lopez\nLeo Martin\nMaya Nguyen\nNoah Okafor\nOlivia Patel\nPablo Quinn\nRosa Silva\nSam Taylor\nTara Usman\nVictor Wong",
  exampleApart: "Ben Carter, Jamal King\nElla Fischer, Rosa Silva",
  exampleFront: "Hugo Ito, Maya Nguyen",
  loadExample: "Load example",
  studentCount: "one:{count} student|other:{count} students",
  seatCount: "one:{count} seat|other:{count} seats",
  classLabel: "Class name",
  classPlaceholder: "Period 3 — Biology",
  layoutLabel: "Room layout",
  layoutRows: "Rows of desks",
  layoutTables: "Tables",
  rowsLabel: "Rows",
  columnsLabel: "Desks per row",
  tablesLabel: "Tables",
  seatsPerTableLabel: "Seats per table",
  tablesPerRowLabel: "Tables per row",
  orderLabel: "Arrangement",
  orderRandom: "Random",
  orderFirst: "A–Z by first name",
  orderLast: "A–Z by last name",
  apartLabel: "Keep apart — one group per line, names separated by commas",
  apartPlaceholder: "Ben Carter, Jamal King",
  frontLabel: "Front row — names separated by commas or new lines",
  frontPlaceholder: "Hugo Ito, Maya Nguyen",
  diagonal: "Count desks that touch at a corner as next to each other",
  arrange: "Arrange seats",
  rearrange: "Rearrange",
  viewLabel: "View",
  viewTeacher: "Teacher view (front at the bottom)",
  viewStudent: "Student view (front at the top)",
  printTeacher: "Print teacher view",
  printStudent: "Print student view",
  frontOfRoom: "Front of room",
  tableName: "Table {number}",
  emptySeat: "Empty",
  seatAria: "{place}: {name}",
  rowSeat: "Row {row}, desk {seat}",
  tableSeat: "Table {table}, seat {seat}",
  swapHint:
    "To swap two students, drag one onto the other, or select a seat and then select another. An empty seat takes the student you move.",
  selected: "{name} selected. Choose another seat to swap with.",
  swapped: "Swapped {a} and {b}.",
  moved: "Moved {name} to an empty seat.",
  needNames: "Enter at least two names.",
  notEnoughSeats:
    "{students} students but only {seats} seats. Add rows, desks or tables.",
  stale:
    "The names or the room changed. Click Arrange seats to update the chart.",
  unknownNames: "Not on the class list, so ignored: {names}",
  duplicates:
    "These names appear more than once: {names}. Add an initial so keep-apart and front-row requests can tell them apart.",
  violationApart: "{a} and {b} are seated together.",
  violationFront: "{name} is not in the front row.",
  allMet: "Every keep-apart and front-row request is met.",
} as const;

type Strings = typeof SEATING_CHART_GENERATOR_STRINGS;

interface Room {
  kind: LayoutKind;
  rows: string;
  columns: string;
  tables: string;
  seatsPerTable: string;
  tablesPerRow: string;
}

function layoutOf(room: Room): SeatingLayout {
  return room.kind === "rows"
    ? { kind: "rows", rows: Number(room.rows), columns: Number(room.columns) }
    : {
        kind: "tables",
        tables: Number(room.tables),
        seatsPerTable: Number(room.seatsPerTable),
        tablesPerRow: Number(room.tablesPerRow),
      };
}

interface Arranged {
  key: string;
  assignment: Assignment;
}

function seatPlace(
  seat: Seat,
  seats: readonly Seat[],
  strings: Strings
): string {
  if (seat.table === null) {
    return t(strings.rowSeat, { row: seat.row + 1, seat: seat.column + 1 });
  }
  const first = seats.findIndex((other) => other.table === seat.table);
  return t(strings.tableSeat, {
    table: seat.table + 1,
    seat: seat.index - first + 1,
  });
}

interface ChartProps {
  /** Buttons on screen; plain boxes for the printed copy. */
  interactive: boolean;
  seats: readonly Seat[];
  names: readonly string[];
  assignment: Assignment;
  view: View;
  selected: number | null;
  strings: Strings;
  onSeat: (index: number) => void;
  onDrop: (from: number, to: number) => void;
}

function SeatButton({ seat, props }: { seat: Seat; props: ChartProps }) {
  const student = props.assignment[seat.index] ?? null;
  const name = student === null ? "" : (props.names[student] ?? "");
  const place = seatPlace(seat, props.seats, props.strings);
  return (
    <button
      aria-label={t(props.strings.seatAria, {
        place,
        name: name || props.strings.emptySeat,
      })}
      aria-pressed={props.selected === seat.index}
      className={`min-h-14 rounded-md border px-2 py-1 text-center text-sm leading-tight transition-colors ${
        props.selected === seat.index
          ? "border-primary bg-primary/10"
          : "border-border bg-card hover:bg-muted"
      } ${name ? "" : "text-muted-foreground"}`}
      draggable={name.length > 0}
      onClick={() => props.onSeat(seat.index)}
      onDragOver={(event) => event.preventDefault()}
      onDragStart={(event: DragEvent<HTMLButtonElement>) =>
        event.dataTransfer.setData("text/plain", String(seat.index))
      }
      onDrop={(event) => {
        event.preventDefault();
        props.onDrop(
          Number(event.dataTransfer.getData("text/plain")),
          seat.index
        );
      }}
      type="button"
    >
      {name || props.strings.emptySeat}
    </button>
  );
}

/** The printed seat: the site's print stylesheet hides every button, so paper gets a box. */
function SeatBox({ seat, props }: { seat: Seat; props: ChartProps }) {
  const student = props.assignment[seat.index] ?? null;
  return (
    <div className="flex min-h-14 items-center justify-center border border-black px-2 py-1 text-center text-sm leading-tight">
      {student === null ? "" : props.names[student]}
    </div>
  );
}

function SeatCell({ seat, props }: { seat: Seat; props: ChartProps }) {
  return props.interactive ? (
    <SeatButton props={props} seat={seat} />
  ) : (
    <SeatBox props={props} seat={seat} />
  );
}

function Chart(props: ChartProps) {
  const rotated = props.view === "teacher";
  const rows = seatRows(props.seats, rotated);
  const perRow = Math.max(1, ...props.seats.map((seat) => seat.column + 1));
  const front = (
    <div className="rounded-md border border-current border-dashed py-1 text-center font-medium text-xs uppercase tracking-wide">
      {props.strings.frontOfRoom}
    </div>
  );
  return (
    <div className="space-y-3">
      {props.view === "student" ? front : null}
      {rows.map((row) => {
        const tables = row.filter(
          (seat, index) =>
            seat.table !== null &&
            row.findIndex((other) => other.table === seat.table) === index
        );
        if (tables.length === 0) {
          return (
            <div
              className="grid gap-2"
              key={row[0]?.index}
              style={{
                gridTemplateColumns: `repeat(${row.length}, minmax(0, 1fr))`,
              }}
            >
              {row.map((seat) => (
                <SeatCell key={seat.index} props={props} seat={seat} />
              ))}
            </div>
          );
        }
        return (
          <div
            className="grid gap-4"
            key={row[0]?.index}
            style={{
              gridTemplateColumns: `repeat(${perRow}, minmax(0, 1fr))`,
            }}
          >
            {tables.map((first) => (
              <div
                className="space-y-2 rounded-lg border border-border p-2 print:border-black"
                key={first.table}
                style={{
                  gridColumnStart: rotated
                    ? perRow - first.column
                    : first.column + 1,
                }}
              >
                <p className="text-center font-medium text-xs">
                  {t(props.strings.tableName, {
                    number: (first.table ?? 0) + 1,
                  })}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {row
                    .filter((seat) => seat.table === first.table)
                    .map((seat) => (
                      <SeatCell key={seat.index} props={props} seat={seat} />
                    ))}
                </div>
              </div>
            ))}
          </div>
        );
      })}
      {props.view === "teacher" ? front : null}
    </div>
  );
}

function RoomControls({
  room,
  strings,
  onChange,
}: {
  room: Room;
  strings: Strings;
  onChange: (patch: Partial<Room>) => void;
}) {
  const select = (key: keyof typeof CHOICES, label: string) => (
    <SelectField
      id={`sc-${key}`}
      label={label}
      onChange={(value) => onChange({ [key]: value })}
      options={CHOICES[key].map((value) => ({ value, label: value }))}
      value={room[key]}
    />
  );
  return (
    <div className="space-y-3">
      <PillGroup
        label={strings.layoutLabel}
        name="sc-layout"
        onChange={(kind) => onChange({ kind })}
        options={[
          { value: "rows", label: strings.layoutRows },
          { value: "tables", label: strings.layoutTables },
        ]}
        value={room.kind}
      />
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        {room.kind === "rows" ? select("rows", strings.rowsLabel) : null}
        {room.kind === "rows" ? select("columns", strings.columnsLabel) : null}
        {room.kind === "tables" ? select("tables", strings.tablesLabel) : null}
        {room.kind === "tables"
          ? select("seatsPerTable", strings.seatsPerTableLabel)
          : null}
        {room.kind === "tables"
          ? select("tablesPerRow", strings.tablesPerRowLabel)
          : null}
      </div>
    </div>
  );
}

function problems(
  names: string[],
  seats: readonly Seat[],
  strings: Strings
): string {
  if (names.length < 2) {
    return strings.needNames;
  }
  return names.length > seats.length
    ? t(strings.notEnoughSeats, {
        students: names.length,
        seats: seats.length,
      })
    : "";
}

export default function SeatingChartGeneratorWidget() {
  const strings = useToolUi(SEATING_CHART_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const [namesInput, setNamesInput] = useState("");
  const [className, setClassName] = useState("");
  const [room, setRoom] = useState<Room>({
    kind: "rows",
    rows: "5",
    columns: "6",
    tables: "6",
    seatsPerTable: "4",
    tablesPerRow: "3",
  });
  const [order, setOrder] = useState<SeatingOrder>("random");
  const [apartText, setApartText] = useState("");
  const [frontText, setFrontText] = useState("");
  const [diagonal, setDiagonal] = useState(true);
  const [view, setView] = useState<View>("teacher");
  const [arranged, setArranged] = useState<Arranged | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [announcement, setAnnouncement] = useState("");

  const names = parseLines(namesInput);
  const layout = layoutOf(room);
  const seats = buildSeats(layout);
  const key = JSON.stringify([names, layout]);
  const constraints = resolveConstraints(names, apartText, frontText, locale);
  const blocking = problems(names, seats, strings);
  const repeated = duplicateNames(names, locale);
  const current = arranged?.key === key ? arranged.assignment : null;
  const violations = current
    ? findViolations(current, seats, constraints, diagonal, names.length)
    : [];

  function arrange() {
    setSelected(null);
    setAnnouncement("");
    setArranged({
      key,
      assignment: arrangeStudents(names, seats, constraints, {
        order,
        diagonal,
        random: secureRandom,
        locale,
      }),
    });
  }

  function nameAt(assignment: Assignment, seat: number): string {
    const student = assignment[seat] ?? null;
    return student === null ? strings.emptySeat : (names[student] ?? "");
  }

  function swap(from: number, to: number) {
    if (!current || from === to || Number.isNaN(from)) {
      return;
    }
    const a = current[from] ?? null;
    const b = current[to] ?? null;
    if (a === null || b === null) {
      const student = a ?? b;
      setAnnouncement(
        student === null ? "" : t(strings.moved, { name: names[student] ?? "" })
      );
    } else {
      setAnnouncement(
        t(strings.swapped, { a: names[a] ?? "", b: names[b] ?? "" })
      );
    }
    setArranged({ key, assignment: swapSeats(current, from, to) });
    setSelected(null);
  }

  function onSeat(index: number) {
    if (selected === null) {
      setSelected(index);
      setAnnouncement(
        t(strings.selected, { name: current ? nameAt(current, index) : "" })
      );
      return;
    }
    if (selected === index) {
      setSelected(null);
      setAnnouncement("");
      return;
    }
    swap(selected, index);
  }

  function print(next: View) {
    flushSync(() => {
      setView(next);
      setSelected(null);
    });
    window.print();
  }

  const describe = (student: number) => names[student] ?? "";
  const chartProps = {
    assignment: current ?? [],
    names,
    onDrop: swap,
    onSeat,
    seats,
    selected,
    strings,
    view,
  };

  return (
    <div className="space-y-6">
      <style>{"@page { size: landscape; }"}</style>
      <div className="space-y-4 print:hidden">
        <TextAreaField
          hint={tPlural(strings.studentCount, locale, names.length, {
            count: names.length,
          })}
          id="sc-names"
          label={strings.namesLabel}
          onChange={setNamesInput}
          placeholder={strings.namesPlaceholder}
          prose
          value={namesInput}
        />
        <button
          className={BUTTON_SMALL}
          onClick={() => {
            setNamesInput(strings.example);
            setApartText(strings.exampleApart);
            setFrontText(strings.exampleFront);
          }}
          type="button"
        >
          {strings.loadExample}
        </button>
        <TextField
          id="sc-class"
          label={strings.classLabel}
          onChange={setClassName}
          placeholder={strings.classPlaceholder}
          value={className}
        />
        <RoomControls
          onChange={(patch) =>
            setRoom((previous) => ({ ...previous, ...patch }))
          }
          room={room}
          strings={strings}
        />
        <p className={HINT}>
          {tPlural(strings.seatCount, locale, seats.length, {
            count: seats.length,
          })}
        </p>
        <PillGroup
          label={strings.orderLabel}
          name="sc-order"
          onChange={setOrder}
          options={[
            { value: "random", label: strings.orderRandom },
            { value: "first-name", label: strings.orderFirst },
            { value: "last-name", label: strings.orderLast },
          ]}
          value={order}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextAreaField
            id="sc-apart"
            label={strings.apartLabel}
            onChange={setApartText}
            placeholder={strings.apartPlaceholder}
            prose
            rows={3}
            value={apartText}
          />
          <TextAreaField
            id="sc-front"
            label={strings.frontLabel}
            onChange={setFrontText}
            placeholder={strings.frontPlaceholder}
            prose
            rows={3}
            value={frontText}
          />
        </div>
        {room.kind === "rows" ? (
          <Checkbox
            checked={diagonal}
            label={strings.diagonal}
            onChange={setDiagonal}
          />
        ) : null}
        <button
          className={BUTTON_PRIMARY}
          disabled={blocking.length > 0}
          onClick={arrange}
          type="button"
        >
          {arranged ? strings.rearrange : strings.arrange}
        </button>
      </div>

      <div aria-live="polite" className="space-y-3 print:hidden">
        {blocking && names.length > 0 ? (
          <ErrorNotice>{blocking}</ErrorNotice>
        ) : null}
        {repeated.length > 0 ? (
          <Notice>
            {t(strings.duplicates, { names: repeated.join(", ") })}
          </Notice>
        ) : null}
        {constraints.unknown.length > 0 ? (
          <Notice>
            {t(strings.unknownNames, { names: constraints.unknown.join(", ") })}
          </Notice>
        ) : null}
        {arranged && !current ? <Notice>{strings.stale}</Notice> : null}
        {violations.map((violation) => (
          <Notice key={JSON.stringify(violation)}>
            {violation.kind === "apart"
              ? t(strings.violationApart, {
                  a: describe(violation.students[0]),
                  b: describe(violation.students[1]),
                })
              : t(strings.violationFront, {
                  name: describe(violation.student),
                })}
          </Notice>
        ))}
        {current &&
        violations.length === 0 &&
        (constraints.apart.length > 0 || constraints.front.length > 0) ? (
          <p className={HINT}>{strings.allMet}</p>
        ) : null}
        {announcement ? <p className={HINT}>{announcement}</p> : null}
      </div>

      {current ? (
        <section className="space-y-4 text-foreground print:text-black">
          <div className="flex flex-wrap items-end justify-between gap-3 print:hidden">
            <PillGroup
              label={strings.viewLabel}
              name="sc-view"
              onChange={setView}
              options={[
                { value: "teacher", label: strings.viewTeacher },
                { value: "student", label: strings.viewStudent },
              ]}
              value={view}
            />
            <div className="flex flex-wrap gap-3">
              <button
                className={BUTTON_SECONDARY}
                onClick={() => print("teacher")}
                type="button"
              >
                {strings.printTeacher}
              </button>
              <button
                className={BUTTON_SECONDARY}
                onClick={() => print("student")}
                type="button"
              >
                {strings.printStudent}
              </button>
            </div>
          </div>
          <p className={`${HINT} print:hidden`}>{strings.swapHint}</p>
          {className.trim() ? (
            <h2 className="font-semibold text-xl">{className.trim()}</h2>
          ) : null}
          <div className="print:hidden">
            <Chart {...chartProps} interactive />
          </div>
          <div className="hidden print:block">
            <Chart {...chartProps} interactive={false} />
          </div>
        </section>
      ) : null}
    </div>
  );
}
